import React, { useState } from 'react';
import { ArrowLeft, Plus, TrendingDown, Pencil, Trash2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { formatAmount, getContactFromTransaction } from '../utils/format';
import Modal from '../components/Modal';

const TransactionDetail = () => {
  const {
    selectedAccount,
    selectedTransaction,
    loading,
    error,
    setScreen,
    setError,
    setLoading,
    deleteTransaction,
    getAccountName
  } = useApp();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleBackFromTransaction = () => {
    if (selectedAccount) {
      setScreen('accountDetail');
    } else {
      setScreen('dashboard');
    }
  };

  const getTransactionDisplay = (tx, currentAccountId = null) => {
    if (!tx) return { isNegative: false, displayAmount: 0 };

    let amount = Math.abs(parseFloat(tx.amount));
    let isNegative = false;

    if (tx.type === 'transfer' && tx.fromAccount && tx.toAccount) {
      if (currentAccountId) {
        if (tx.fromAccount.id === currentAccountId) {
          isNegative = true;
        } else if (tx.toAccount.id === currentAccountId) {
          isNegative = false;
        }
      } else {
        isNegative = false;
      }
    } else if (tx.type === 'income') {
      isNegative = false;
    } else if (tx.type === 'loan') {
      isNegative = parseFloat(tx.amount) < 0;
    } else if (tx.type === 'settlement') {
      isNegative = true;
    } else if (tx.type === 'expense') {
      isNegative = true;
    } else {
      isNegative = parseFloat(tx.expenseAmount) > 0;
    }

    return { isNegative, displayAmount: amount };
  };

  const handleDelete = async () => {
    try {
      await deleteTransaction(selectedTransaction.id);
      setShowDeleteConfirm(false);
      if (selectedAccount) {
        setScreen('accountDetail');
      } else {
        setScreen('dashboard');
      }
    } catch (err) {
      // Error handled in context
    }
  };

  if (!selectedTransaction) {
    setScreen('dashboard');
    return null;
  }

  const { isNegative, displayAmount } = getTransactionDisplay(selectedTransaction, selectedAccount?.id);
  const contact = getContactFromTransaction(selectedTransaction);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 pb-20">
      <div className="bg-white shadow-sm p-4 flex items-center">
        <button onClick={handleBackFromTransaction} className="mr-4">
          <ArrowLeft className="w-6 h-6 text-gray-600" />
        </button>
        <h1 className="text-xl font-bold text-gray-800">Transaction Details</h1>
      </div>

      <div className="p-6 space-y-4">
        {/* Amount Display */}
        <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
          <p className="text-sm text-gray-500 mb-2">Amount</p>
          <p className={`text-4xl font-bold ${isNegative ? 'text-red-600' : 'text-green-600'}`}>
            {isNegative ? '-' : '+'}${formatAmount(displayAmount)}
          </p>
          {isNegative ? (
            <p className="text-sm text-red-500 mt-2">Money Out</p>
          ) : (
            <p className="text-sm text-green-500 mt-2">Money In</p>
          )}
        </div>

        {/* Transaction Info */}
        <div className="bg-white rounded-2xl p-6 shadow-lg space-y-4">
          <div>
            <p className="text-xs text-gray-500 uppercase mb-1">Description</p>
            <p className="text-lg font-semibold text-gray-800">{selectedTransaction.description}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase mb-1">Date</p>
              <p className="text-sm font-medium text-gray-800">{selectedTransaction.date}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase mb-1">Type</p>
              <p className="text-sm font-medium text-gray-800 capitalize">{selectedTransaction.type}</p>
            </div>
          </div>

          {selectedTransaction.status && (
            <div>
              <p className="text-xs text-gray-500 uppercase mb-1">Status</p>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                selectedTransaction.status === 'cleared'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-yellow-100 text-yellow-700'
              }`}>
                {selectedTransaction.status}
              </span>
            </div>
          )}

          {selectedTransaction.tags && (
            <div>
              <p className="text-xs text-gray-500 uppercase mb-1">Tags</p>
              <div className="flex flex-wrap gap-2">
                {selectedTransaction.tags.split(',').map((tag, idx) => (
                  <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                    {tag.trim()}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Account Info */}
        {selectedTransaction.type === 'transfer' && selectedTransaction.fromAccount && selectedTransaction.toAccount ? (
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <p className="text-xs text-gray-500 uppercase mb-3">Transfer Details</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">From Account</p>
                  <p className="font-semibold text-gray-800">{selectedTransaction.fromAccount.name}</p>
                </div>
                <div className="text-red-600 font-bold">-${formatAmount(displayAmount)}</div>
              </div>

              <div className="flex justify-center">
                <TrendingDown className="w-6 h-6 text-gray-400" />
              </div>

              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">To Account</p>
                  <p className="font-semibold text-gray-800">{selectedTransaction.toAccount.name}</p>
                </div>
                <div className="text-green-600 font-bold">+${formatAmount(displayAmount)}</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <p className="text-xs text-gray-500 uppercase mb-2">Account</p>
            <p className="font-semibold text-gray-800">{selectedTransaction.accountName || getAccountName(selectedTransaction.accountId)}</p>
          </div>
        )}

        {/* Loan/Settlement Contact Info */}
        {(selectedTransaction.type === 'loan' || selectedTransaction.type === 'settlement') && contact && (
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <p className="text-xs text-gray-500 uppercase mb-2">
              {selectedTransaction.type === 'settlement' ? 'Settlement With' : 'Contact'}
            </p>
            <p className="font-semibold text-gray-800">{contact}</p>
          </div>
        )}

        {/* Additional Info */}
        {selectedTransaction.extraInfo && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
            <p className="text-sm text-blue-800">{selectedTransaction.extraInfo}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={() => setScreen('editTransaction')}
            className="flex-1 bg-indigo-600 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-indigo-700 transition"
          >
            <Pencil className="w-5 h-5" />
            Edit
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex-1 bg-red-600 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-red-700 transition"
          >
            <Trash2 className="w-5 h-5" />
            Delete
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
        <div className="text-center mb-6">
          <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trash2 className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Delete Transaction?</h3>
          <p className="text-gray-600">This action cannot be undone. Are you sure you want to delete this transaction?</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowDeleteConfirm(false)}
            className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={loading}
            className="flex-1 bg-red-600 text-white py-3 rounded-xl font-semibold hover:bg-red-700 transition disabled:bg-gray-400"
          >
            {loading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
        {error && (
          <div className="mt-4 bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}
      </Modal>

      <button
        onClick={() => setScreen('add')}
        className="fixed bottom-6 right-6 bg-indigo-600 text-white p-4 rounded-full shadow-lg hover:bg-indigo-700 transition"
      >
        <Plus className="w-8 h-8" />
      </button>
    </div>
  );
};

export default TransactionDetail;
